/**
 * Build-time configuration.
 *
 * Everything here is public, non-secret display configuration. The frontend
 * holds no credential and no OAuth client: sign-in is server-side, and the one
 * link the browser needs comes from `GET /api/v1/auth/config` at runtime.
 */

/**
 * Where a researcher reports a console that cannot see the machines.
 *
 * Shown on the screens that render *before* the server has answered — a
 * console whose API is down cannot tell the browser who to call, so this one
 * string has to be in the bundle. Everything reachable after the server
 * answers uses `access_contact` from `GET /api/v1/auth/config` instead, which
 * a deployment can change without rebuilding this image.
 *
 * "the platform team" rather than a channel name: `#neuralk-alerts` is the
 * Alertmanager firehose and nobody watches it for questions, and inventing a
 * channel that does not exist is worse than naming a group of people.
 */
export const SUPPORT_CHANNEL = "the platform team";

/**
 * Who the console acts as in the server's development mode.
 *
 * `AUTH_MODE=dev` authenticates nobody: it trusts an `X-User` header
 * (`docs/AUTH.md` §1), so the console sends one fixed name and every screen
 * carries the banner saying what that means. The default is the account the
 * dev stack also treats as an admin, so the approval screens work on a laptop
 * with no Google client.
 *
 * `VITE_DEV_USER` changes that name at dev-server start
 * (`make web VITE_DEV_USER=marie`). It exists because the approval gate has two
 * sides and one identity can only ever show you one of them: to see what a
 * researcher waiting for approval sees, you have to *be* one. It is read only
 * when the server reports dev mode, and a production build of the API ignores
 * the header entirely.
 */
export const DEV_MODE_USERNAME = import.meta.env.VITE_DEV_USER || "admin";

/** Poll period for the home screen: capacity and your workspace. */
export const HOME_POLL_MS = 5_000;

/** Poll period for a workspace that is not usable yet. */
export const WORKSPACE_ACTIVE_POLL_MS = 3_000;

/** Poll period for a workspace that is already usable. */
export const WORKSPACE_IDLE_POLL_MS = 15_000;

/** Poll period for the jobs list and for a job that is still moving. */
export const JOB_POLL_MS = 10_000;

/** Poll period for the CPU runs list and for a run that is still moving. */
export const CPU_RUN_POLL_MS = 10_000;

/** Log lines requested for one task of a CPU run. */
export const CPU_RUN_LOG_TAIL = 500;

/**
 * Task rows asked for per page of a run's task list.
 *
 * The API's own default, said out loud rather than left to the server: the
 * "show more" button names the number it will add, and a button whose count
 * came from somewhere else would be lying about the click.
 */
export const CPU_TASK_PAGE_SIZE = 100;

/** Poll period for the admin screen: accounts and live workspaces. */
export const ADMIN_POLL_MS = 10_000;

/**
 * How long *Training elsewhere* stays fresh, in milliseconds.
 *
 * Nothing about it moves on its own: an address from configuration, a key the
 * researcher sets themselves. There is nothing to poll for — the machine's own
 * sync is what changes, and the console cannot see it (`docs/AUTH.md` §4b).
 */
export const RELAY_STALE_MS = 300_000;

/** How many audit lines the admin screen asks for. */
/** Rows per page of the audit log — a screen, not a dump. */
export const AUDIT_LIMIT = 50;

/** Startup log lines requested for a workspace. */
export const WORKSPACE_LOG_TAIL = 200;

/** Log lines requested for a job. */
export const JOB_LOG_TAIL = 500;

/**
 * Workspace duration fallbacks, used only when the server publishes none.
 *
 * They mirror `DEFAULT_WORKSPACE_HOURS` / `MAX_WORKSPACE_HOURS` in
 * `docs/CONVENTIONS.md`; the server value always wins when present.
 */
export const FALLBACK_WORKSPACE_HOURS = 8;
export const FALLBACK_MAX_WORKSPACE_HOURS = 24;
